import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { QuestionSet } from '../../types';
import {
  Plus,
  Layers,
  Edit2,
  Trash2,
  Clock,
  Shuffle,
  HelpCircle,
  X,
  ArrowRight,
  BookOpen,
  RotateCcw,
} from 'lucide-react';
import { useToast } from '../ui/Toast';
import { ConfirmModal } from '../common/ConfirmModal';

export const QuestionSetManager: React.FC = () => {
  const {
    sectors,
    subjects,
    questionSets,
    questions,
    addQuestionSet,
    updateQuestionSet,
    deleteQuestionSet,
    clearQuestionsInSet,
    setCurrentView,
  } = useApp();
  const { showToast } = useToast();

  const [deleteSetTarget, setDeleteSetTarget] = useState<QuestionSet | null>(null);
  const [clearSetTarget, setClearSetTarget] = useState<QuestionSet | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const confirmDeleteSet = () => {
    if (!deleteSetTarget) return;
    deleteQuestionSet(deleteSetTarget.id);
    showToast('Question set deleted', 'info');
    setDeleteSetTarget(null);
  };

  const confirmClearSet = async () => {
    if (!clearSetTarget) return;
    const count = questions.filter((q) => q.question_set_id === clearSetTarget.id).length;
    const ok = await clearQuestionsInSet(clearSetTarget.id);
    if (ok) {
      showToast(`Cleared all ${count} questions from "${clearSetTarget.name}"`, 'info');
    } else {
      showToast('Failed to clear questions', 'error');
    }
    setClearSetTarget(null);
  };
  const [editingSet, setEditingSet] = useState<QuestionSet | null>(null);

  const [formData, setFormData] = useState({
    sector_id: sectors[0]?.id || 1,
    subject_id: subjects[0]?.id || 1,
    name: '',
    description: '',
    instructions: '',
    status: 'published' as 'published' | 'draft',
    random_questions: false,
    random_options: false,
    time_limit_minutes: 10,
  });

  const handleOpenAdd = () => {
    setEditingSet(null);
    setFormData({
      sector_id: sectors[0]?.id || 1,
      subject_id: subjects[0]?.id || 1,
      name: '',
      description: '',
      instructions: '',
      status: 'published',
      random_questions: false,
      random_options: false,
      time_limit_minutes: 10,
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (qs: QuestionSet) => {
    setEditingSet(qs);
    setFormData({
      sector_id: qs.sector_id,
      subject_id: qs.subject_id,
      name: qs.name,
      description: qs.description,
      instructions: qs.instructions,
      status: qs.status,
      random_questions: qs.random_questions,
      random_options: qs.random_options,
      time_limit_minutes: qs.time_limit_minutes,
    });
    setIsModalOpen(true);
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    if (editingSet) {
      updateQuestionSet(editingSet.id, formData);
      showToast('Question set updated', 'success');
    } else {
      addQuestionSet(formData);
      showToast('New question set published', 'success');
    }
    setIsModalOpen(false);
  };

  return (
    <div className="space-y-6 pb-24 md:pb-16">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-[#111111] tracking-tight">
            Question Sets & Exam Modules
          </h1>
          <p className="text-xs sm:text-sm text-[#5F6368]">
            Configure test packages, set durations, candidate instructions, and publishing status.
          </p>
        </div>

        <button
          onClick={handleOpenAdd}
          className="px-4 py-2.5 rounded-xl bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold flex items-center gap-1.5 shadow-xs transition-all self-start sm:self-auto cursor-pointer"
        >
          <Plus className="w-4 h-4 text-[#111111]" />
          New Question Set
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {questionSets.map((qs) => {
          const parentSector = sectors.find((s) => s.id === qs.sector_id);
          const parentSubject = subjects.find((sub) => sub.id === qs.subject_id);
          const qCount = questions.filter((q) => q.question_set_id === qs.id).length;

          return (
            <div
              key={qs.id}
              className="bg-white rounded-2xl border border-[#E5E9E9] p-5 flex flex-col justify-between hover:border-[#56E8E1]/60 transition-all shadow-[0_2px_12px_rgba(17,17,17,0.02)]"
            >
              <div>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <span className="px-2.5 py-0.5 rounded-md bg-[#FFF7D9] text-[#8a6508] border border-[#EBC138]/40 text-[11px] font-semibold">
                    {parentSubject?.name || 'Subject'}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenEdit(qs)}
                      className="p-1.5 rounded-lg text-[#5F6368] hover:text-[#1b8e88] hover:bg-[#E8FAF9] transition-colors cursor-pointer"
                      title="Edit set"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setClearSetTarget(qs)}
                      disabled={qCount === 0}
                      className={`p-1.5 rounded-lg transition-colors cursor-pointer ${
                        qCount === 0
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-[#5F6368] hover:text-[#D97706] hover:bg-[#FEF3C7]'
                      }`}
                      title={
                        qCount === 0
                          ? 'No questions to clear'
                          : `Clear all ${qCount} questions in this set`
                      }
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteSetTarget(qs)}
                      className="p-1.5 rounded-lg text-[#5F6368] hover:text-[#D94A5C] hover:bg-[#FDEDEF] transition-colors cursor-pointer"
                      title="Delete set"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                <h3 className="text-base font-bold text-[#111111] mb-1">{qs.name}</h3>
                <p className="text-xs text-[#5F6368] line-clamp-2 mb-4 leading-relaxed">
                  {qs.description || 'No description provided.'}
                </p>

                <div className="grid grid-cols-2 gap-2 text-[11px] text-[#111111] bg-[#F7FAFA] p-3 rounded-xl mb-4 border border-[#E5E9E9]">
                  <div className="flex items-center gap-1.5">
                    <HelpCircle className="w-3.5 h-3.5 text-[#1b8e88]" />
                    <span>{qCount} Questions</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-[#1b8e88]" />
                    <span>{qs.time_limit_minutes} min test</span>
                  </div>
                  <div className="flex items-center gap-1.5 col-span-2">
                    <Shuffle className="w-3.5 h-3.5 text-[#8a6508]" />
                    <span className="text-[#5F6368]">
                      {qs.random_questions ? 'Shuffled questions' : 'Sequential order'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t border-[#E5E9E9] flex items-center justify-between">
                <span
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    qs.status === 'published'
                      ? 'bg-[#EAF8F1] text-[#20A66A] border border-[#20A66A]/30'
                      : 'bg-[#F0F4F4] text-[#5F6368] border border-[#E5E9E9]'
                  }`}
                >
                  {qs.status}
                </span>

                <button
                  onClick={() => setCurrentView('admin-questions')}
                  className="text-xs font-bold text-[#1b8e88] hover:text-[#146e69] flex items-center gap-1 cursor-pointer"
                >
                  View MCQs
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-[#111111]/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-7 shadow-2xl border border-[#E5E9E9]">
            <div className="flex items-center justify-between pb-3 border-b border-[#E5E9E9] mb-4">
              <h3 className="text-base font-bold text-[#111111]">
                {editingSet ? 'Edit Question Set' : 'New Question Set'}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-[#5F6368] hover:text-[#111111] p-1 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Sector
                  </label>
                  <select
                    value={formData.sector_id}
                    onChange={(e) =>
                      setFormData({ ...formData, sector_id: Number(e.target.value) })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                  >
                    {sectors.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Subject
                  </label>
                  <select
                    value={formData.subject_id}
                    onChange={(e) =>
                      setFormData({ ...formData, subject_id: Number(e.target.value) })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                  >
                    {subjects.map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">
                  Set Title *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Coding-Decoding — Set 2"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">
                  Description
                </label>
                <input
                  type="text"
                  placeholder="Brief summary of syllabus or topics covered..."
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-[#111111] mb-1">
                  Candidate Exam Instructions
                </label>
                <textarea
                  rows={2}
                  placeholder="Instructions displayed to candidates prior to starting..."
                  value={formData.instructions}
                  onChange={(e) =>
                    setFormData({ ...formData, instructions: e.target.value })
                  }
                  className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Exam Duration (Minutes)
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={180}
                    value={formData.time_limit_minutes}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        time_limit_minutes: Number(e.target.value) || 10,
                      })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#111111] mb-1">
                    Publish Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.value as 'published' | 'draft',
                      })
                    }
                    className="w-full px-3 py-2 rounded-xl border border-[#E5E9E9] text-xs focus:outline-none focus:border-[#56E8E1] bg-white"
                  >
                    <option value="published">Published (Visible to Students)</option>
                    <option value="draft">Draft (Hidden)</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2 pt-2 border-t border-[#E5E9E9]">
                <label className="flex items-center gap-2 text-xs font-semibold text-[#111111] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.random_questions}
                    onChange={(e) =>
                      setFormData({ ...formData, random_questions: e.target.checked })
                    }
                    className="rounded accent-[#56E8E1] focus:ring-[#56E8E1]"
                  />
                  <span>Randomize question sequence for candidates</span>
                </label>

                <label className="flex items-center gap-2 text-xs font-semibold text-[#111111] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.random_options}
                    onChange={(e) =>
                      setFormData({ ...formData, random_options: e.target.checked })
                    }
                    className="rounded accent-[#56E8E1] focus:ring-[#56E8E1]"
                  />
                  <span>Shuffle options (A, B, C, D) order</span>
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#E5E9E9]">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-semibold text-[#5F6368] hover:bg-[#F0F4F4] rounded-xl transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] text-xs font-bold rounded-xl transition-all shadow-xs cursor-pointer"
                >
                  {editingSet ? 'Save Changes' : 'Create Question Set'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteSetTarget}
        title="Delete Question Set"
        message={
          deleteSetTarget
            ? `Delete "${deleteSetTarget.name}"? Questions inside will remain in the bank.`
            : ''
        }
        confirmText="Delete Question Set"
        variant="danger"
        onConfirm={confirmDeleteSet}
        onCancel={() => setDeleteSetTarget(null)}
      />

      <ConfirmModal
        isOpen={!!clearSetTarget}
        title="Clear Questions"
        message={
          clearSetTarget
            ? `This will permanently remove all ${
                questions.filter((q) => q.question_set_id === clearSetTarget.id).length
              } questions from ${clearSetTarget.name}. The Question Set itself will NOT be deleted.`
            : ''
        }
        confirmText="Clear Questions"
        variant="danger"
        onConfirm={confirmClearSet}
        onCancel={() => setClearSetTarget(null)}
      />
    </div>
  );
};
